import { CheckableTreeInput } from '@framework/form/inputs/CheckableTreeInput.tsx';
import { IsRadioOrCheckboxGroupElementSymbol } from '@framework/form/item/symbols/isRadioOrCheckboxGroupElement.tsx';
import { Tooltip } from 'antd';
import { type CSSProperties, type ReactNode, useMemo } from 'react';
import { type IMapGroup } from '../MapGroup.ts';
import { type IMapLayer } from '../MapLayer.ts';
import { buildMapLayersTreeData } from './buildMapLayersTreeData.ts';

export interface IMapLayersTreeInputProps {
  /** Nome del campo di form che contiene le chiavi spuntate (`string[]`). */
  name: string;
  groups: IMapGroup[];
  layers: IMapLayer[];
  /** Invocata dopo l'aggiornamento del field, con le chiavi spuntate. */
  onAfterChange?: (value: string[]) => void;
  /** Espande tutti i nodi all'apertura (import catalogo). */
  defaultExpandAll?: boolean;
  /** Chiavi espanse all'apertura (legenda, da expandedByDefault). */
  defaultExpandedKeys?: string[];
  /** Personalizza il titolo della foglia layer (default: `layer.name`). */
  renderLayerTitle?: (layer: IMapLayer) => ReactNode;
  /** Mostra il conteggio dei livelli accanto al nome del gruppo. */
  showCounts?: boolean;
  /** Etichetta della sezione dei livelli senza gruppo. */
  ungroupedLabel?: string;
  style?: CSSProperties;
}

/**
 * Input di form ad albero checkable di gruppi e layer della mappa. Costruisce
 * i nodi da `groups`/`layers` (via {@link buildMapLayersTreeData}) e delega la
 * selezione a {@link CheckableTreeInput}: il valore del campo `name` è l'array
 * di chiavi spuntate.
 *
 * È il componente "MapTree" dedicato alla mappa, riusato sia dalla legenda
 * runtime sia dall'anteprima di import catalogo.
 */
export function MapLayersTreeInput(props: IMapLayersTreeInputProps) {
  const {
    name,
    groups,
    layers,
    onAfterChange,
    defaultExpandAll,
    defaultExpandedKeys,
    renderLayerTitle,
    showCounts,
    ungroupedLabel,
    style,
  } = props;

  const treeData = useMemo(
    () =>
      buildMapLayersTreeData(groups, layers, {
        renderLayerTitle,
        renderGroupTitle: (group, label) =>
          group.description ? (
            <Tooltip title={group.description}>
              <span>{label}</span>
            </Tooltip>
          ) : (
            label
          ),
        showCounts,
        ungroupedLabel,
      }),
    [groups, layers, renderLayerTitle, showCounts, ungroupedLabel],
  );

  return (
    <CheckableTreeInput
      name={name}
      onAfterChange={onAfterChange}
      treeData={treeData}
      defaultExpandAll={defaultExpandAll}
      defaultExpandedKeys={defaultExpandedKeys}
      style={style}
    />
  );
}

// È un input "di gruppo" (albero di checkbox, nessun input singolo): lo segnala
// a FormItem, che ne associa la label via `aria-labelledby` anziché `for`.
MapLayersTreeInput[IsRadioOrCheckboxGroupElementSymbol] = true;
